import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, Users, DollarSign, Award, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getGoogleAdsClient, getDateRange } from '../lib/google-ads-client';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

interface CompetitorMetrics {
  domain: string;
  impressionShare: number;
  overlapRate: number;
  positionAboveRate: number;
  topOfPageRate: number;
  absoluteTopRate: number;
  outRankingShare: number;
}

export default function AuctionInsightsDashboardPage() {
  const { company, setCompany } = useGoogleAdsCompany();
  const [loading, setLoading] = useState(true);
  const [competitors, setCompetitors] = useState<CompetitorMetrics[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [myMetrics, setMyMetrics] = useState<CompetitorMetrics | null>(null);

  useEffect(() => {
    loadData();
  }, [company]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (!company) {
        setCompetitors([]);
        setMyMetrics(null);
        return;
      }

      const { data: customerData } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (customerData) {
        await loadAuctionInsights(customerData.customer_id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAuctionInsights = async (customerId: string) => {
    const range = getDateRange(30);
    const { data } = await supabase
      .from('google_ads_auction_insights')
      .select('*')
      .gte('date', range.startDate)
      .lte('date', range.endDate)
      .order('impression_share', { ascending: false });

    if (data && data.length > 0) {
      const grouped = groupByDomain(data);
      setCompetitors(grouped.filter(c => !c.domain.includes('Your domain')));
      setMyMetrics(grouped.find(c => c.domain.includes('Your domain')) || null);
    }
  };

  const groupByDomain = (data: any[]): CompetitorMetrics[] => {
    const domains = new Map<string, any[]>();
    data.forEach(row => {
      if (!domains.has(row.competitor_domain)) {
        domains.set(row.competitor_domain, []);
      }
      domains.get(row.competitor_domain)?.push(row);
    });

    return Array.from(domains.entries()).map(([domain, rows]) => ({
      domain,
      impressionShare: rows.reduce((sum, r) => sum + r.impression_share, 0) / rows.length,
      overlapRate: rows.reduce((sum, r) => sum + r.overlap_rate, 0) / rows.length,
      positionAboveRate: rows.reduce((sum, r) => sum + r.position_above_rate, 0) / rows.length,
      topOfPageRate: rows.reduce((sum, r) => sum + r.top_of_page_rate, 0) / rows.length,
      absoluteTopRate: rows.reduce((sum, r) => sum + r.absolute_top_rate, 0) / rows.length,
      outRankingShare: rows.reduce((sum, r) => sum + r.outranking_share, 0) / rows.length
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Auction Insights Dashboard</h1>
        <p className="text-gray-600 mt-1">Competitive landscape analysis and market positioning</p>
      </div>

      {myMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Your Impression Share</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{myMetrics.impressionShare.toFixed(1)}%</p>
              </div>
              <Target className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Top of Page Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{myMetrics.topOfPageRate.toFixed(1)}%</p>
              </div>
              <Award className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Absolute Top Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{myMetrics.absoluteTopRate.toFixed(1)}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Competitors</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{competitors.length}</p>
              </div>
              <Users className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Competitor Analysis</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impression Share</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overlap Rate</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Position Above</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Top of Page</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Absolute Top</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {competitors.map((comp, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{comp.domain}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{comp.impressionShare.toFixed(1)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{comp.overlapRate.toFixed(1)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{comp.positionAboveRate.toFixed(1)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{comp.topOfPageRate.toFixed(1)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{comp.absoluteTopRate.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
